import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useHistory } from "react-router-dom";

import {
  ActiveLearningAnswersProgress,
  IActiveLearningMaterialLessonsData,
} from "../../../state";
import DropdownMenu, {
  Option as DropdownOption,
} from "../../../atoms/DropdownMenu";
import Button from "../../../atoms/Button";
import { ReactComponent as Groups } from "../../../assets/svg/groups.svg";
import { ReactComponent as Download } from "../../../assets/svg/file_download.svg";
import { ReactComponent as Progress } from "../../../assets/svg/progress.svg";
import { parseQueryParams, zeroPadding } from "../../../libs/Util";
import { activeLearningLessonPath } from "../../../teacherStore";
import Spinner from "../../../atoms/Spinner";

import styles from "./ActiveLearningLessonRetrospect.module.scss";

type Props = {
  activeLearningMaterialLessons: IActiveLearningMaterialLessonsData[];
  activeLearningAnswersProgress: ActiveLearningAnswersProgress[];
  availableItemCodes: string[];
  calling: boolean;
  csvDownloadCalling: boolean;
  onClickDownloadAnswersCsv: (class_ids: string, material_code: string) => void;
};

const ActiveLearningLessonRetrospect = ({
  activeLearningMaterialLessons,
  activeLearningAnswersProgress,
  availableItemCodes,
  calling,
  csvDownloadCalling,
  onClickDownloadAnswersCsv,
}: Props) => {
  const history = useHistory();

  const params = parseQueryParams();
  const classListInParams = params["classList"];

  const roundOption: DropdownOption[] = [...availableItemCodes]
    .sort()
    .map(c => {
      const round = c.slice(-1);
      return {
        label: `${round}周目`,
        value: Number(round),
        data: c,
      };
    });

  const [studentAnswers, setStudentAnswers] = useState({
    code: roundOption[0].data,
    lesson: 1,
  });

  const lessonOption: DropdownOption[] = useMemo(
    () =>
      activeLearningMaterialLessons
        .filter(m => m.code === studentAnswers.code && m.lesson.no !== 0)
        .map(m => ({
          label: `Lesson${zeroPadding(m.lesson.no)}`,
          value: m.lesson.no,
        })),
    [activeLearningMaterialLessons, studentAnswers.code],
  );

  const handleClickViewStudentAnswers = () => {
    history.push(
      `${activeLearningLessonPath}/${studentAnswers.lesson}?classList=${classListInParams}&code=${studentAnswers.code}&from=lessonRetrospect`,
    );
  };

  const [csvAnswersCode, setCsvAnswersCode] = useState(roundOption[0].data);

  const [progressRoundCode, setProgressRoundCode] = useState(
    roundOption[0].data,
  );

  const lessons = useMemo(
    () =>
      activeLearningMaterialLessons
        .filter(m => m.code === progressRoundCode && m.lesson.no !== 0)
        .map(m => m.lesson),
    [activeLearningMaterialLessons, progressRoundCode],
  );

  const questions = useMemo(
    () =>
      lessons.flatMap(l =>
        l.issues.flatMap(i =>
          i.questions.map(q => ({
            q_id: q.id,
            l_no: l.no,
            i_no: i.no,
            q_no: q.order_num,
          })),
        ),
      ),
    [lessons],
  );

  const answeredKeysByStudent = useMemo(
    () =>
      new Map(
        activeLearningAnswersProgress.map(p => [
          p.id,
          new Set(
            p.answers.flatMap(a =>
              a.answered_questions.map(
                q => `${a.code}-${q.lesson_no}-${q.issue_no}-${q.question_no}`,
              ),
            ),
          ),
        ]),
      ),
    [activeLearningAnswersProgress],
  );

  // ディスプレイやブラウザ拡大率によってborderサイズに差異があるためヘッダーの位置を算出する
  const tableRef = useRef<HTMLTableElement>(null);
  useEffect(() => {
    const adjustHeaderTop = () => {
      const table = tableRef.current;
      if (!table) return;

      const headerRows = table.querySelectorAll("thead tr");
      let currentTop = 0;

      headerRows.forEach(row => {
        const ths = row.querySelectorAll("th");

        ths.forEach(th => {
          th.style.top = `${currentTop}px`;
        });

        currentTop += row.getBoundingClientRect().height;
      });
    };

    adjustHeaderTop();
    window.addEventListener("resize", adjustHeaderTop);
    return () => window.removeEventListener("resize", adjustHeaderTop);
  }, [calling, progressRoundCode]);

  const viewStudentAnswersPanel = () => (
    <div className={styles.panel}>
      <div className={styles.panel__title}>
        <Groups />
        <p>生徒の回答を見る</p>
      </div>
      <p className={styles.panel__description}>
        Lessonごとの生徒の回答はこちらからご確認ください。
      </p>
      <div className={styles.optionContainer}>
        <div className={styles.dropdownContainer}>
          <DropdownMenu
            className={styles.dropdown}
            options={roundOption}
            onChange={(_, data) => {
              setStudentAnswers({
                code: data,
                lesson: 1,
              });
            }}
          />
          <DropdownMenu
            key={studentAnswers.code} // 周回が変わったらlessonの表示をリセットする
            className={styles.lessonDropdown}
            options={lessonOption}
            onChange={value => {
              setStudentAnswers({
                ...studentAnswers,
                lesson: value,
              });
            }}
          />
        </div>
        <Button
          name="回答を見る"
          variant="normal"
          className={styles.button}
          onClick={handleClickViewStudentAnswers}
        />
      </div>
    </div>
  );

  const downloadAnswersPanel = () => (
    <div className={styles.panel}>
      <div className={styles.panel__title}>
        <Download />
        <p>回答csvダウンロード</p>
      </div>
      <p className={styles.panel__description}>
        全Lesson分のCSVデータはこちらからダウンロードできます。
      </p>
      <div className={styles.optionContainer}>
        <DropdownMenu
          className={styles.dropdown}
          options={roundOption}
          onChange={(_, data) => {
            setCsvAnswersCode(data);
          }}
        />
        <Button
          name={csvDownloadCalling ? "ダウンロード中..." : "全回答ダウンロード"}
          className={styles.csvDownloadButton}
          onClick={() =>
            onClickDownloadAnswersCsv(classListInParams, csvAnswersCode)
          }
          disabled={csvDownloadCalling}
        >
          <Download />
        </Button>
      </div>
    </div>
  );

  const lessonProgressPanel = () => (
    <div className={styles.panel}>
      <div className={styles.panel__header}>
        <div className={styles.panel__title}>
          <Progress />
          <p>授業進捗</p>
          <span className={styles.panel__description}>
            回答が入力されているWORKを色付きで表示しています。進捗の把握や声掛けにお使いください。
          </span>
        </div>
        <div className={styles.switchContainer}>
          {roundOption.map(round => {
            return (
              <button
                key={round.value}
                className={clsx(
                  styles.switch,
                  round.data === progressRoundCode && styles.selected,
                )}
                onClick={() => {
                  setProgressRoundCode(round.data);
                }}
              >
                {round.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className={styles.progressTableWrapper}>
        <div className={styles.progressTableContainer}>
          <table ref={tableRef}>
            <thead>
              <tr className={styles.lessonHeader}>
                <th>Lesson</th>
                {lessons.map(l => {
                  const totalColumns = l.issues.reduce(
                    (sum, issue) => sum + issue.questions.length,
                    0,
                  );
                  return (
                    <th key={l.id} colSpan={totalColumns}>
                      {l.no}
                    </th>
                  );
                })}
              </tr>
              <tr className={styles.workHeader}>
                <th>WORK</th>
                {lessons.map(l =>
                  l.issues.map(issue => {
                    const colSpan = issue.questions.length;
                    return (
                      <th
                        key={issue.id}
                        colSpan={colSpan}
                        className={clsx(
                          issue.no === 1 && styles.firstLessonWork,
                        )}
                      >
                        {issue.no}
                      </th>
                    );
                  }),
                )}
              </tr>
              <tr className={styles.questionHeader}>
                <th />
                {questions.map(q => (
                  <th
                    key={q.q_id}
                    className={clsx(
                      q.i_no === 1 && q.q_no === 1 && styles.firstLessonWork,
                    )}
                  >{`${q.i_no}-${q.q_no}`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeLearningAnswersProgress.map(progress => {
                return (
                  <tr key={progress.id}>
                    <td className={styles.name}>{progress.full_name}</td>
                    {questions.map(q => {
                      const hasAnswered = answeredKeysByStudent
                        .get(progress.id)
                        ?.has(
                          `${progressRoundCode}-${q.l_no}-${q.i_no}-${q.q_no}`,
                        );
                      return hasAnswered ? (
                        <td key={q.q_id} className={styles.hasAnswered} />
                      ) : (
                        <td key={q.q_id} className={styles.nonAnswered} />
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (calling) {
    return (
      <div className={styles.loading}>
        <Spinner spinColor="black" />
        <p className={styles.loadingText}>データ取得中...</p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <p className={styles.title}>CORE 実践編 授業毎データ</p>
      {answeredKeysByStudent.size === 0 ? (
        <p className={styles.emptyMessage}>
          担当しているクラスに生徒が登録されていません。
        </p>
      ) : (
        <>
          <div className={styles.panelContainer}>
            {viewStudentAnswersPanel()}
            {downloadAnswersPanel()}
          </div>
          {lessonProgressPanel()}
        </>
      )}
    </div>
  );
};

export default ActiveLearningLessonRetrospect;
